import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { RootStateCampanha } from '../../store/campanhaReducer';
import { ResetResgatesCheckout, ResgatesCheckout } from 'domain/store/_carteira/campanhaPedidoResgate/actions/campanhaPedidoResgateAction';
import { GetCarteiraUsuario } from 'domain/store/_carteira/carteiraBase/actions/carteiraBaseAction';
import PagarPedidoContainer, { OBTER_NOTA_REPASSE, ENVIAR_EMAIL_NOTA } from '../../containers/pagarPedido/pagarPedidoContainer';
import StateMensage from 'base/src/view/states/state';
import { DownloadHtml } from 'base/src/components/DownloadHtml';
import { Button } from 'base/src/components/Button';
import { Modal, ModalContent, ModalTrigger } from 'base/src/components/Modal';
import ViewContainer from '../viewContainer/viewContainer';
import * as S from './notaRepasseStyle';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    TaxaPagamento: state.CarteiraBaseReducer.CarteiraBase.TaxaPagamento,
    Pagar: state.CampanhaPedidoResgateReducer.formData,
    CampanhaPedidoResgate: state.CampanhaPedidoResgateReducer.CampanhaPedidoResgateByCampanha,
  }),
  { GetCarteiraUsuario, ResgatesCheckout, ResetResgatesCheckout }
);

export interface CampanhaComponentProps extends PropsFromRedux<typeof connector> {
}

function NotaRepasse() {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [searchParams] = useSearchParams();
  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const [emailDestino, setEmailDestino] = useState<string>('');
  const [mensagemAdicional, setMensagemAdicional] = useState<string>('');
  const [enviandoEmail, setEnviandoEmail] = useState<boolean>(false);
  const _pagarPedidoContainer = new PagarPedidoContainer();

  // Pegar o ID do parâmetro da URL
  const campanhaPedidoResgatePagamentoId = Number(searchParams.get('CampanhaPedidoResgatePagamentoID'));
  const campanhaId = Number(searchParams.get('CampanhaID'));

  useEffect(() => {
    if (campanhaPedidoResgatePagamentoId) {
      carregarNotaRepasse();
    }
  }, [campanhaPedidoResgatePagamentoId]);

  async function carregarNotaRepasse() {
    setLoading(true);
    setError('');

    try {
      const response = await _pagarPedidoContainer.obterNotaRepasse(campanhaPedidoResgatePagamentoId);

      if (response instanceof Error) {
        setError('Erro ao carregar nota de repasse');
      } else {
        setHtmlContent(response);
      }
    } catch (err) {
      setError('Erro ao carregar nota de repasse');
    } finally {
      setLoading(false);
    }
  }

  async function enviarEmail() {
    if (!emailDestino) {
      alert('Por favor, informe o e-mail de destino');
      return;
    }

    setEnviandoEmail(true);
    try {
      await _pagarPedidoContainer.enviarEmailNotaRepasse(
        campanhaPedidoResgatePagamentoId,
        campanhaId,
        emailDestino,
        mensagemAdicional
      );
      handleModalChange(false);
      setEmailDestino('');
      setMensagemAdicional('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao enviar e-mail:', error);
    } finally {
      setEnviandoEmail(false);
    }
  }

  function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
    setEmailDestino(event.target.value);
  }

  function handleMensagemChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setMensagemAdicional(event.target.value);
  }

  function handleCloseModal() {
    handleModalChange(false);
  }

  function handleModalChange(open: boolean) {
    setEmailModalOpen(open);
    if (open) {
      // Limpar qualquer estado de loading anterior do envio de e-mail
      _pagarPedidoContainer.clearEmailLoadingState();
    }
  }

  return (
    <ViewContainer>
      <S.Content>
        <StateMensage stateErrorNames={[OBTER_NOTA_REPASSE]} stateLoadingNames={[OBTER_NOTA_REPASSE]} />

        {error && (
          <S.ErrorMessage role="alert" aria-live="assertive">
            {error}
          </S.ErrorMessage>
        )}

        {htmlContent && !loading && !error && (
          <S.HtmlContent>
            <S.PreviewFrame
              srcDoc={htmlContent}
              title="Nota de Repasse"
              tabIndex={0}
            />
          </S.HtmlContent>
        )}

        {htmlContent && !loading && !error && (
          <S.DownloadSection>
            <DownloadHtml
              print={true}
              htmlContent={htmlContent}
              fileName={`nota_repasse_${campanhaPedidoResgatePagamentoId}.html`}
              preview={true}
            />

            <Modal open={emailModalOpen} onOpenChange={handleModalChange}>
              <ModalTrigger asChild>
                <Button size="sm" iconName="ph:envelope">
                  Enviar por E-mail
                </Button>
              </ModalTrigger>
              <ModalContent>
                <S.EmailModalContent>
                  <h3>Enviar Nota por E-mail</h3>
                  <StateMensage stateLoadingNames={[ENVIAR_EMAIL_NOTA]} />

                  <S.FormGroup>
                    <label>E-mail de destino:</label>
                    <S.Input
                      type="email"
                      value={emailDestino}
                      onChange={handleEmailChange}
                      placeholder="exemplo@email.com"
                      required
                    />
                  </S.FormGroup>

                  <S.FormGroup>
                    <label>Mensagem adicional (opcional):</label>
                    <S.TextArea
                      value={mensagemAdicional}
                      onChange={handleMensagemChange}
                      placeholder="Digite uma mensagem adicional que será incluída no e-mail..."
                      rows={3}
                    />
                  </S.FormGroup>

                  <S.ButtonGroup>
                    <Button size="md" variant="secondary" onClick={handleCloseModal}>
                      Cancelar
                    </Button>
                    <Button
                      size="md"
                      onClick={enviarEmail}
                      disabled={enviandoEmail || !emailDestino}
                      iconName={enviandoEmail ? "ph:spinner" : "ph:envelope"}
                    >
                      {enviandoEmail ? 'Enviando...' : 'Enviar E-mail'}
                    </Button>
                  </S.ButtonGroup>
                </S.EmailModalContent>
              </ModalContent>
            </Modal>
          </S.DownloadSection>
        )}
      </S.Content>
    </ViewContainer>
  )
}

export default connector(NotaRepasse);
